import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { OrganizationSchema, WebSiteSchema, WebPageSchema, BreadcrumbListSchema, ArticleSchema } from '@/components/base/SeoSchema';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.slug === slug) || fallbackArticle;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200/70">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-6">该文章不存在或已被删除</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number): string => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return String(v);
  };

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleUrl = `http://smallbar.cn/article/${slug}`;

  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        title={`${article.title} - 51在线吃瓜`}
        description={article.excerpt}
        url={articleUrl}
        datePublished={article.date}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'http://smallbar.cn/' },
          { name: article.category, url: `http://smallbar.cn/category/${article.categorySlug}` },
          { name: article.title, url: articleUrl },
        ]}
      />
      <ArticleSchema
        title={article.title}
        description={article.excerpt}
        url={articleUrl}
        imageUrl={article.imageUrl}
        datePublished={article.date}
        authorName="51在线吃瓜编辑部"
        category={article.category}
        tags={article.tags}
      />
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="w-full pt-24 pb-10 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-foreground-500 text-xs mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-950 line-clamp-1">{article.title}</span>
            </nav>

            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium mb-4 whitespace-nowrap">
              {article.category}
            </span>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 leading-snug mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-foreground-500 text-sm mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(article.views)} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-chat-1-line"></i>
                评论
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-share-forward-line"></i>
                分享
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-800 rounded-full text-xs font-medium whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="w-full bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <div className="rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 51在线吃瓜`}
                className="w-full h-80 md:h-96 object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="w-full py-10 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-foreground-800 text-base md:text-lg leading-relaxed space-y-5">
              {article.content.split('\n\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-thumb-up-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-star-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line text-lg"></i>
                  </button>
                </div>
                <div className="flex items-center gap-2 text-foreground-500 text-sm">
                  <span>声明：本文内容来源于网络爆料，不代表本站立场</span>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-10">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel, idx) => (
                  <Link key={rel.id} to={`/article/${rel.slug}`} className="block h-full">
                    <article className={`rounded-lg overflow-hidden group cursor-pointer h-full flex flex-col ${idx === 0 ? 'bg-foreground-900' : 'bg-background-50'}`}>
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={rel.imageUrl}
                          alt={rel.title}
                          title={`${rel.title} - 51在线吃瓜`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                            {rel.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 flex-1 flex flex-col justify-between">
                        <h3 className={`text-base font-medium leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2 ${idx === 0 ? 'text-background-50' : 'text-foreground-950'}`}>
                          {rel.title}
                        </h3>
                        <p className={`text-sm leading-relaxed line-clamp-2 ${idx === 0 ? 'text-background-400' : 'text-foreground-600'}`}>
                          {rel.excerpt}
                        </p>
                        <div className={`flex items-center justify-between mt-3 text-xs ${idx === 0 ? 'text-background-500' : 'text-foreground-500'}`}>
                          <span>{rel.date}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {formatViews(rel.views)}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Download */}
        <section className="relative w-full min-h-[400px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20wide%20panoramic%20abstract%20composition%20with%20warm%20gradients%20from%20deep%20charcoal%20to%20rich%20coral%20and%20amber%20tones%20soft%20diffused%20ambient%20light%20with%20subtle%20geometric%20texture%20modern%20minimalist%20style%20cinematic%20atmosphere%20suitable%20for%20a%20background%20no%20text%20no%20people%20just%20atmospheric%20color%20gradients%20and%20subtle%20patterns&width=1600&height=600&seq=cta-article-download&orientation=landscape"
              alt="下载51在线吃瓜APP"
              title="51在线吃瓜app下载"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-heading text-background-50 leading-tight mb-6">
                下载51在线吃瓜APP
              </h2>
              <a
                href="#"
                className="inline-flex items-center gap-3 px-8 py-3.5 bg-background-50 text-foreground-950 rounded-full text-sm font-medium hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>立即下载</span>
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-foreground-950">
                  <i className="ri-arrow-right-line text-background-50 text-sm"></i>
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}